import { useState, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface WithdrawalPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerified: () => void;
  userId: string;
  onAccountSuspended: () => void;
}

export default function WithdrawalPasswordModal({
  isOpen,
  onClose,
  onVerified,
  userId,
  onAccountSuspended,
}: WithdrawalPasswordModalProps) {
  const [password, setPassword] = useState("");
  const [errorCount, setErrorCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [verifying, setVerifying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleVerify = async () => {
    if (!password || password.length < 4) {
      setErrorMsg("請輸入提款密碼");
      return;
    }
    if (!userId) {
      setErrorMsg("用戶資訊異常，請重新登入");
      return;
    }

    setVerifying(true);
    setErrorMsg("");

    try {
      // Fetch stored withdraw_password from profiles
      const { data, error } = await supabase
        .from("profiles")
        .select("withdraw_password, withdraw_error_count, account_status")
        .eq("id", userId)
        .maybeSingle();

      if (error) {
        console.error("[WithdrawalPasswordModal] select error:", error);
        setErrorMsg("驗證失敗，請重試");
        setVerifying(false);
        return;
      }

      // Account already suspended
      if (data?.account_status === "suspended") {
        setVerifying(false);
        onAccountSuspended();
        return;
      }

      const storedPassword = data?.withdraw_password || "";

      if (password !== storedPassword) {
        const newErrorCount = (data?.withdraw_error_count || 0) + 1;

        // Update error count in DB
        const { error: updateErr } = await supabase
          .from("profiles")
          .update({ withdraw_error_count: newErrorCount })
          .eq("id", userId);

        if (updateErr) {
          console.error("[WithdrawalPasswordModal] error count update error:", updateErr);
        }

        setErrorCount(newErrorCount);

        if (newErrorCount >= 3) {
          // Suspend account (aligns with admin "暫停" status)
          const { error: suspendErr } = await supabase
            .from("profiles")
            .update({ account_status: "suspended", withdraw_error_count: 0 })
            .eq("id", userId);

          if (suspendErr) {
            console.error("[WithdrawalPasswordModal] suspend error:", suspendErr);
          }
          setVerifying(false);
          onAccountSuspended();
          return;
        }

        setErrorMsg("密碼錯誤，請重新輸入");
        setPassword("");
        setVerifying(false);
        inputRef.current?.focus();
        return;
      }

      // Password correct — reset error count
      const { error: resetErr } = await supabase
        .from("profiles")
        .update({ withdraw_error_count: 0 })
        .eq("id", userId);

      if (resetErr) {
        console.error("[WithdrawalPasswordModal] reset error count error:", resetErr);
      }

      setErrorCount(0);
      setPassword("");
      setVerifying(false);
      onVerified();
    } catch (err: unknown) {
      console.error("[WithdrawalPasswordModal] exception:", err);
      const msg = err instanceof Error ? err.message : "驗證過程發生錯誤";
      setErrorMsg(msg);
      setVerifying(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-6 py-5 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-stone-800">
              <i className="ri-shield-keyhole-line text-rose-700 mr-2" />
              安全驗證
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-stone-400" />
            </button>
          </div>
          <p className="font-body text-xs text-stone-500 mt-1">
            請輸入提款密碼以確認本次提款申請
          </p>
        </div>

        <div className="p-6 flex flex-col gap-4">
          <div>
            <label className="font-body text-xs font-medium text-stone-500 mb-1.5 block">
              提款密碼
            </label>
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => {
                const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 6);
                setPassword(val);
                setErrorMsg("");
              }}
              onKeyDown={handleKeyDown}
              placeholder="請輸入提款密碼"
              maxLength={6}
              autoFocus
              className="w-full font-body text-lg tracking-[0.3em] px-4 py-3 rounded-xl border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none transition-all text-center placeholder:tracking-normal placeholder:text-stone-300"
            />
          </div>

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm mt-0.5" />
              <p className="font-body text-xs text-red-600">{errorMsg}</p>
            </div>
          )}

          <button
            onClick={handleVerify}
            disabled={verifying || password.length < 4}
            className="w-full font-body text-sm font-semibold px-5 py-3 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {verifying ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                驗證中...
              </span>
            ) : (
              "確認提款"
            )}
          </button>

          <button
            onClick={onClose}
            className="w-full font-body text-sm font-medium px-5 py-2.5 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  );
}